import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Chip, LoadingScreen } from '@cowoo/shared/ui-web';
import { formatDate, formatMoney, qk } from '@cowoo/shared';
import { api } from '../../lib/api';
import { EmptyState, FilterChips, ListCard, Page, Row, Table, Td, Th } from '../../ui';
import { InvoicePanel } from './InvoicePanel';
import { CHIPS, STATUS_TONE, periodLabel, type InvoiceChip } from './invoiceRow';

/** § 13 opens on the current month. */
const thisMonth = (): string => new Date().toISOString().slice(0, 7);

/**
 * `/rechnungen` (10-web-admin § 13).
 *
 * Invoices are never edited or deleted (D-046), so there is no edit UI here and
 * never will be — a correction is a credit note, which is its own document.
 *
 * `?user=` comes from the member panel, so "4 Rechnungen" there lands on that
 * member's four rather than on everybody's.
 */
export function Invoices() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { invoiceId } = useParams();
  const [search] = useSearchParams();
  const userId = search.get('user');
  const [status, setStatus] = useState<InvoiceChip>('all');
  const [month, setMonth] = useState(thisMonth);
  const [q, setQ] = useState('');

  const invoices = useQuery({
    queryKey: qk.admin.area('invoices', { status, month, q, userId }),
    queryFn: async () => {
      const { data, error } = await api.GET('/admin/invoices', {
        params: {
          query: {
            ...(status === 'all' ? {} : { status }),
            ...(month ? { month } : {}),
            ...(q ? { q } : {}),
            ...(userId ? { userId } : {}),
          },
        },
      });
      if (error) throw error;
      return data;
    },
  });

  if (invoices.isLoading) return <LoadingScreen label={t('common.loading')} />;

  const items = invoices.data?.items ?? [];

  return (
    <Page>
      <ListCard
        title={t('admin.invoices.title')}
        actions={
          <>
            <input
              type="month"
              value={month}
              onChange={(event) => setMonth(event.target.value)}
              aria-label={t('admin.invoices.month')}
              className="border-line rounded-input text-adminBody h-9 border bg-white px-3 font-semibold"
            />
            <input
              value={q}
              onChange={(event) => setQ(event.target.value)}
              placeholder={t('admin.invoices.search')}
              className="border-line rounded-input text-adminBody h-9 w-[230px] border px-3"
            />
          </>
        }
      >
        <FilterChips
          value={status}
          onChange={setStatus}
          options={CHIPS.map((value) => ({
            value,
            label: t(`admin.invoices.chip.${value}`),
            count: invoices.data?.counts[value],
          }))}
        />

        {items.length === 0 ? (
          <EmptyState>{t('admin.invoices.empty')}</EmptyState>
        ) : (
          <Table
            head={
              <>
                <Th>{t('admin.invoices.column.number')}</Th>
                <Th>{t('admin.invoices.column.date')}</Th>
                <Th>{t('admin.invoices.column.customer')}</Th>
                <Th>{t('admin.invoices.column.period')}</Th>
                <Th align="right">{t('admin.invoices.column.net')}</Th>
                <Th align="right">{t('admin.invoices.column.vat')}</Th>
                <Th align="right">{t('admin.invoices.column.gross')}</Th>
                <Th>{t('admin.invoices.column.status')}</Th>
              </>
            }
          >
            {items.map((invoice) => (
              <Row key={invoice.id} to={`/rechnungen/${invoice.id}`} selected={invoice.id === invoiceId}>
                <Td bold>{invoice.number}</Td>
                <Td>{formatDate(invoice.issuedOn)}</Td>
                <Td>{[invoice.customerNumber, invoice.customerName].filter(Boolean).join(' · ') || '—'}</Td>
                <Td>{periodLabel(invoice)}</Td>
                <Td numeric>{formatMoney(invoice.netCents)}</Td>
                <Td numeric>{formatMoney(invoice.vatCents)}</Td>
                <Td numeric>{formatMoney(invoice.grossCents)}</Td>
                <Td>
                  <Chip
                    tone={STATUS_TONE[invoice.status] ?? 'neutral'}
                    label={t(`admin.invoices.state.${invoice.status}`)}
                  />
                </Td>
              </Row>
            ))}
          </Table>
        )}
      </ListCard>

      {invoiceId ? <InvoicePanel id={invoiceId} onClose={() => navigate('/rechnungen')} /> : null}
    </Page>
  );
}
